import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart, updateQty, cartTotal, tableStore } from "@/lib/cart";
import { formatMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import FoodImg from "@/components/FoodImg";

export default function Cart() {
  const cart = useCart();
  const navigate = useNavigate();
  const tbl = tableStore.get();
  // Explicit destination, not navigate(-1): survives refresh / deep-link / UPI app-switch.
  const backToMenu = () => navigate(tbl ? `/m/${tbl.token}` : "/");

  return (
    <div className="min-h-screen bg-[var(--qd-bg)] qd-grain pb-32">
      <header className="p-5 flex items-center justify-between">
        <button data-testid="cart-back" onClick={backToMenu} aria-label="Back to menu" className="w-11 h-11 rounded-full bg-white grid place-items-center border border-[var(--qd-border)]"><ArrowLeft className="w-4 h-4" aria-hidden /></button>
        <h1 className="font-display font-bold text-xl">Your Cart</h1>
        <div className="w-11" />
      </header>

      {cart.length === 0 ? (
        <div className="text-center px-8 py-24">
          <ShoppingBag className="w-16 h-16 mx-auto text-[var(--qd-muted)]" aria-hidden />
          <p className="mt-4 text-[var(--qd-muted)]">Your cart is empty</p>
          {tbl
            ? <Link to={`/m/${tbl.token}`} data-testid="cart-back-to-menu"><Button className="mt-6 rounded-full bg-[var(--qd-primary)] hover:bg-[var(--qd-primary-dark)]">Browse Menu</Button></Link>
            : <Link to="/" data-testid="cart-home"><Button className="mt-6 rounded-full bg-[var(--qd-primary)] hover:bg-[var(--qd-primary-dark)]">Go to QRDine home</Button></Link>}
        </div>
      ) : (
        <div className="px-5 space-y-3">
          {cart.map((it, i) => (
            <div key={`${it.menu_item_id}-${i}`} data-testid={`cart-item-${i}`} className="flex gap-3 bg-white rounded-2xl p-3 qd-shadow-soft border border-[var(--qd-border)]">
              <FoodImg src={it.image_url} alt={it.name} w={200} className="w-20 h-20 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                {/* 2-line clamp, not truncate: the stepper column leaves ~90px here on 375px
                    screens, so single-line truncation cut names to "Kadak Ma…". */}
                <div className="font-semibold line-clamp-2 break-words">{it.name}</div>
                {it.modifiers && it.modifiers.length > 0 && <div className="text-xs text-[var(--qd-muted)] mt-0.5 line-clamp-2">{it.modifiers.map((m) => m.option_name).join(", ")}</div>}
                {it.notes && <div className="text-xs text-[var(--qd-muted)] mt-0.5 line-clamp-1 italic">“{it.notes}”</div>}
                <div className="font-display font-bold mt-1">{formatMoney(it.price * it.quantity)}</div>
              </div>
              <div className="flex flex-col items-end justify-between">
                <button data-testid={`cart-remove-${i}`} onClick={() => updateQty(i, 0)} aria-label={`Remove ${it.name}`} className="p-2 -m-1 rounded-full text-[var(--qd-muted)] hover:text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" aria-hidden /></button>
                <div className="inline-flex items-center gap-1 bg-[var(--qd-cream)] rounded-full p-1">
                  <button data-testid={`cart-minus-${i}`} onClick={() => updateQty(i, it.quantity - 1)} aria-label="Decrease quantity" className="w-10 h-10 rounded-full bg-white grid place-items-center"><Minus className="w-4 h-4" aria-hidden /></button>
                  <span className="text-base font-bold w-6 text-center" aria-live="polite">{it.quantity}</span>
                  <button data-testid={`cart-plus-${i}`} onClick={() => updateQty(i, it.quantity + 1)} aria-label="Increase quantity" className="w-10 h-10 rounded-full bg-white grid place-items-center"><Plus className="w-4 h-4" aria-hidden /></button>
                </div>
              </div>
            </div>
          ))}
          {tbl && (
            <Link to={`/m/${tbl.token}`} data-testid="cart-add-more" className="block text-center text-sm font-semibold text-[var(--qd-primary)] py-4">
              + Add more items
            </Link>
          )}
        </div>
      )}

      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-5 bg-white/95 backdrop-blur border-t border-[var(--qd-border)]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-[var(--qd-muted)]">Subtotal</span>
            <span data-testid="cart-subtotal" className="font-display text-2xl font-bold">{formatMoney(cartTotal())}</span>
          </div>
          <Link to="/checkout" data-testid="cart-checkout-btn">
            <Button className="w-full h-14 rounded-full bg-[var(--qd-primary)] hover:bg-[var(--qd-primary-dark)] text-white font-semibold text-base">Proceed to Checkout →</Button>
          </Link>
        </div>
      )}
    </div>
  );
}
